import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Briefcase, Users, Building2, ArrowRight, Sparkles, CheckCircle } from 'lucide-react';

export function JobBoardSection() {
  return (
    <section className="py-20 px-4 relative z-10 bg-beige">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <Badge className="bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]/30 hover:bg-[#B8860B]/20">
            <Briefcase className="h-3 w-3 mr-1" />
            Conecta Talento
          </Badge>
          <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
            Bolsa de Empleo del Sector Belleza
          </h2>
          <p className="font-body text-[#8C7E6F] max-w-2xl mx-auto">
            La plataforma que conecta profesionales de belleza con los mejores salones
          </p>
        </div>

        {/* Two Columns */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Para Profesionales */}
          <Card className="border border-[#B8860B]/10 hover:border-[#B8860B]/30 rounded-[20px] transition-all duration-300 hover:shadow-lg group bg-white">
            <CardHeader className="pb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#B8860B] to-[#996F0A] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Users className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-center text-2xl font-heading text-[#2B2520]">
                Para Profesionales
              </CardTitle>
              <CardDescription className="text-center font-body text-[#8C7E6F]">
                Encuentra tu próxima oportunidad laboral
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-neutral-700 text-center leading-relaxed">
                Encuentra trabajo en salones de belleza cerca de ti. Crea tu perfil profesional y aplica a ofertas en minutos.
              </p>

              <div className="space-y-3 pt-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-600">
                    Crea tu perfil profesional completo
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-600">
                    Aplica a múltiples ofertas con un click
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-600">
                    Recibe notificaciones de nuevas ofertas
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-600">
                    Filtra por ubicación, especialidad y salario
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <Link to="/empleo" className="block">
                  <Button
                    variant="outline"
                    className="w-full border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-white shadow-sm group"
                  >
                    Buscar Ofertas de Empleo
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Para Negocios */}
          <Card className="border-2 border-[#B8860B]/30 bg-white hover:border-[#B8860B] rounded-[20px] transition-all duration-300 hover:shadow-lg group relative overflow-hidden">
            {/* Badge "Destacado" */}
            <div className="absolute top-4 right-4">
              <Badge className="bg-[#B8860B] text-white shadow-lg">
                <Sparkles className="h-3 w-3 mr-1" />
                Destacado
              </Badge>
            </div>

            <CardHeader className="pb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#B8860B] to-[#996F0A] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-center text-2xl font-heading text-[#2B2520]">
                Para Negocios
              </CardTitle>
              <CardDescription className="text-center font-body text-[#8C7E6F]">
                Encuentra el talento que tu salón necesita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-neutral-700 text-center leading-relaxed">
                Publica ofertas de empleo y encuentra el talento que necesitas. Sistema de créditos flexible sin suscripciones.
              </p>

              <div className="space-y-3 pt-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-700">
                    Publica ofertas ilimitadas con créditos
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-700">
                    Recibe aplicaciones de candidatos cualificados
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-700">
                    Sistema de créditos: paga solo por lo que uses
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-700">
                    Gestiona candidatos desde tu dashboard
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <Link to="/empleo" className="block">
                  <Button
                    className="w-full bg-[#B8860B] hover:bg-[#996F0A] text-white shadow-lg group"
                  >
                    Publicar Oferta de Empleo
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="text-center space-y-4 mt-12">
          <div className="bg-white rounded-[20px] p-8 border border-[#B8860B]/10 shadow-ref-card">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B] to-[#D4A526] flex items-center justify-center flex-shrink-0">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-heading text-[#2B2520]">
                    ¿Listo para encontrar tu próximo talento o trabajo?
                  </h3>
                  <p className="font-body text-sm text-[#8C7E6F]">
                    Únete a la comunidad de profesionales de belleza más grande de España
                  </p>
                </div>
              </div>
              <Link to="/empleo">
                <Button
                  size="lg"
                  className="bg-[#B8860B] hover:bg-[#996F0A] text-white shadow-lg whitespace-nowrap"
                >
                  Ver Bolsa de Empleo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 pt-8">
            <div className="text-center">
              <div className="font-heading text-3xl font-bold text-[#B8860B]">500+</div>
              <div className="font-body text-sm text-[#8C7E6F]">Ofertas Activas</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-3xl font-bold text-[#B8860B]">2,000+</div>
              <div className="font-body text-sm text-[#8C7E6F]">Profesionales</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-3xl font-bold text-[#B8860B]">300+</div>
              <div className="font-body text-sm text-[#8C7E6F]">Salones</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
